import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { type AnyAri } from '@atlassian/ari';
import { type SearchResultConversationPartial } from '@atlassian/search-client';

import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { MessageBodyDeprecated } from '../../../../../../../common/ui/message-body-deprecated';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { type useFireContextMenuButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { type useFireContextMenuCopyButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireSummarizeInChatButtonClicked } from '../../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';

import { i18n } from './messages';

interface ConversationSearchResultProps {
	partial: SearchResultConversationPartial;
	icon: React.ReactNode;
	cloudId?: string;
	position?: number;
	resultAri: AnyAri;
	product?: string;
	onClick: (arg: any) => void;
	onClickCtxMenu: VoidFunction;
	onSummarizeInChatButtonClicked?: ReturnType<typeof useFireSummarizeInChatButtonClicked>;
	onContextMenuCopyButtonClicked?: ReturnType<typeof useFireContextMenuCopyButtonClicked>;
	onContextMenuButtonClicked?: ReturnType<typeof useFireContextMenuButtonClicked>;
	isAllProductSelected?: boolean;
}

export const ConversationSearchResult = ({
	partial,
	icon,
	position,
	onClick,
	onClickCtxMenu,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	product,
	cloudId,
	isAllProductSelected,
}: ConversationSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { entity, url } = partial;
	const [primaryProduct] = usePrimaryProduct();
	const appMode = useApplicationMode();
	const [{ thirdPartyConfigs }] = useBootstrap();

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');

	const memberCount = entity?.memberCount || entity?.members?.length;
	const parts: [string, React.ReactNode][] = [
		['label', <FormattedMessage {...i18n.label} />],
		[
			'memberCount',
			memberCount && <FormattedMessage {...i18n.memberCount} values={{ count: memberCount }} />,
		],
	];

	let privateChannelName;
	if (partial.subtype === 'private-channel') {
		privateChannelName = (
			<>
				<LockLockedIcon label={formatMessage(i18n.privateChannel)} size="small" />
				{entity?.displayName}
			</>
		);
	}

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected
			onResultClicked={onClick}
			onResultContextMenu={onClickCtxMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={
				<MessageBodyDeprecated partial={partial} integrationId={partial.integrationId} />
			}
			icon={icon}
			headerMetadataParts={parts}
			title={privateChannelName || `#${entity?.displayName || formatMessage(i18n.unknownChannel)}`}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
			footer={
				<MentionedByLinkedResults
					linkedResults={partial.linkedResults || []}
					thirdPartyConfigs={thirdPartyConfigs}
					partial={partial}
					url={url}
					position={position}
					onResultClicked={onClick}
					onResultContextMenu={onClickCtxMenu}
					isAllProductSelected={isAllProductSelected}
				/>
			}
		/>
	);
};
